import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, NetworkError } from '@/lib/api.ts';
import { keys } from '@/lib/queries.ts';
import { Focusable } from '@/ui/Focusable.tsx';

/**
 * Four characters on the television, typed on a phone.
 *
 * This replaces pick-a-face-then-type-a-PIN, and it is better on every axis
 * that matters here. That design existed because typing a password with a
 * D-pad is the worst interaction the app has — which was true — and it cost
 * provisioning an account for everybody and handing out a PIN each. This
 * costs nothing: Plex already knows who is allowed, and seventeen people
 * already know this exact flow from every other Plex app they own.
 *
 * NOTHING SECRET PASSES THROUGH THIS SCREEN. The code authorises nothing on
 * its own; the token it eventually produces is exchanged server-side and
 * never reaches the browser, because a token in a WebView on a shared
 * television is a token anything that can run script can read. What lands
 * here is an ordinary session cookie we can revoke.
 */

/** Slow enough to be polite to plex.tv, fast enough to feel instant. */
const POLL_MS = 2_000;

export function PlexLogin() {
  const qc = useQueryClient();
  const [code, setCode] = useState<string | null>(null);
  // Plex's own sign-in page for this sign-in (Google, Apple or a password),
  // and the same address as a QR code drawn by the server.
  const [qr, setQr] = useState<{ url: string; image: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);

  // Held in a ref, not state: the polling loop reads it and a re-render on
  // every tick would be a render per two seconds for a value nothing draws.
  const pinId = useRef<number | null>(null);
  const stopped = useRef(false);

  async function begin() {
    setBusy(true);
    setError(null);
    setExpired(false);
    setCode(null);
    setQr(null);
    try {
      const pin = await api.plexStart();
      pinId.current = pin.id;
      setCode(pin.code);
      if (pin.qrUrl && pin.qrImage) setQr({ url: pin.qrUrl, image: pin.qrImage });
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  }

  // One code on mount. Asking for a second while the first is on screen would
  // invalidate the characters somebody is halfway through typing.
  useEffect(() => {
    void begin();
    return () => { stopped.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!code || pinId.current === null) return;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      if (stopped.current || pinId.current === null) return;
      try {
        const res = await api.plexPoll(pinId.current);
        if (!res.pending) {
          // Signed in. Let the app's own `me` query take it from here rather
          // than routing by hand — the shell already renders on that.
          await qc.invalidateQueries({ queryKey: keys.me });
          return;
        }
      } catch (e) {
        // 410 is the code timing out, which is ordinary and has its own
        // screen. 403 is the access check refusing, which is NOT ordinary and
        // must say so plainly rather than looking like a glitch.
        if (e instanceof ApiError && e.status === 410) { setExpired(true); return; }
        if (e instanceof ApiError && e.status === 403) { setError(e.message); return; }
        // Anything else — a dropped wifi, plex.tv having a moment — is worth
        // retrying rather than abandoning somebody mid-sign-in.
      }
      timer = setTimeout(poll, POLL_MS);
    };

    timer = setTimeout(poll, POLL_MS);
    return () => clearTimeout(timer);
  }, [code, qc]);

  return (
    <div className="center">
      <div className="login">
        <h1>N8Media</h1>

        {error && (
          <>
            <p className="plex-error">{error}</p>
            <Focusable id="plex-retry" autoFocus className="btn primary" onSelect={begin}>
              Try again
            </Focusable>
          </>
        )}

        {!error && expired && (
          <>
            <p className="muted">That code expired.</p>
            <Focusable id="plex-again" autoFocus className="btn primary" onSelect={begin}>
              Get a new code
            </Focusable>
          </>
        )}

        {!error && !expired && qr && (
          <div className="plex-qr">
            {/* On a computer: Plex's sign-in page in a new tab. On a TV
                browser: point a phone at the code. Either claims this
                sign-in, and this screen moves on by itself. */}
            <Focusable id="plex-signin" autoFocus className="btn primary"
              onSelect={() => { window.open(qr.url, '_blank', 'noopener'); }}>
              Sign in with Plex
            </Focusable>
            <p className="muted plex-hint">Google, Apple or your Plex password.</p>
            <img src={qr.image} alt="QR code to sign in with your phone" width={180} height={180} />
            <p className="muted plex-hint">Or scan with your phone.</p>
          </div>
        )}

        {!error && !expired && (
          <>
            <p className="plex-step">
              {qr ? 'Or, on any device, go to ' : 'On your phone, go to '}<strong>plex.tv/link</strong>
            </p>

            {/* The code IS the interface. Large enough to read across a
                living room, spaced so the characters cannot be misread, and
                never a font that confuses O with 0. */}
            <div className="plex-code" aria-label={code ? `Code ${code.split('').join(' ')}` : 'Getting a code'}>
              {busy || !code ? <span className="plex-waiting">…</span> : code}
            </div>

            <p className="muted plex-hint">
              Sign in with the Plex account this server is shared with.
              This screen will move on by itself.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function describe(e: unknown): string {
  if (e instanceof NetworkError) return 'Can’t reach the server — is it running?';
  if (e instanceof ApiError) return e.message;
  return 'Something went wrong getting a code.';
}
